import { responsiveStyle } from '@synoptic/ui-kit/responsive/responsive-style.ts';
import {
  selectionTag,
  selectionTagSize,
} from '@synoptic/ui-kit/selection-tag/selection-tag.css.js';
import { themeVars } from '@synoptic/ui-kit/theme/vars.ts';
import { borderRadius, fontWeight, space } from '@synoptic/ui-kit/tokens.ts';
import { typography } from '@synoptic/ui-kit/typography.ts';
import { style } from '@vanilla-extract/css';

export const streamCard = style({
  height: '100%',
  maxWidth: '100%',
  borderRadius: borderRadius.r12,
  transition: '.2s',
  overflow: 'hidden',

  ':hover': {
    backgroundColor: themeVars.container.primary.background.hover,
  },

  ...responsiveStyle({
    mobile: {
      minHeight: 265,
    },
    tablet: {},
    laptop: {
      minHeight: 94,
    },
  }),
});

export const streamCardLink = style({
  height: '100%',
  display: 'flex',

  ...responsiveStyle({
    mobile: {
      gap: space.s16,
      flexDirection: 'column',
      padding: `${space.s24}px ${space.s32}px`,
    },
    tablet: {},
    laptop: {
      flexDirection: 'row',
      gap: 'unset',
      padding: `${space.s16}px ${space.s24}px`,
    },
    desktop: {
      padding: `${space.s24}px ${space.s32}px`,
    },
  }),
});

export const streamCardLeft = style({
  display: 'flex',
  flexDirection: 'column',
  justifyContent: 'center',
  width: '100%',
  maxWidth: 430,
  marginRight: 'auto',
  minWidth: 0,
  flexGrow: 1,
  gap: space.s4,
});

export const cardTitleContainer = style({
  display: 'flex',
  gap: space.s16,
  alignItems: 'center',
  minHeight: 24,
});

export const streamCardRight = style({
  display: 'flex',
  alignItems: 'center',
  gap: space.s20,
  flexShrink: 0,
  ...responsiveStyle({
    tablet: {},
    laptop: {
      marginLeft: space.s16,
    },
    desktop: {
      marginLeft: space.s24,
    },
  }),
});

export const categoryTagContainer = style([
  {
    display: 'flex',
    width: 75,
  },
]);

export const categoryTag = style([
  typography.labelSm,
  selectionTag,
  selectionTagSize['md'],
  responsiveStyle({
    mobile: {
      width: '100%',
    },
    tablet: {},
    laptop: {
      width: 'fit-content',
    },
  }),
]);

export const streamTag = style([
  responsiveStyle({
    mobile: {
      selectors: {
        '&:is(&)': {
          width: '100%',
        },
      },
    },
    laptop: {
      selectors: {
        '&:is(&)': {
          width: 'fit-content',
        },
      },
    },
  }),
]);

export const streamCardHeader = style([
  {
    display: 'flex',
    justifyContent: 'space-between',
    gap: space.s4,
    alignItems: 'center',
  },
]);

export const mobileHeaderMainContent = style([
  {
    display: 'flex',
    gap: space.s12,
    alignItems: 'center',
  },
]);

export const cardTitle = style({
  ...responsiveStyle({
    mobile: {
      ...typography.titleMd,
      fontWeight: fontWeight.bold,
      display: '-webkit-box',
      overflow: 'hidden',
      textOverflow: 'ellipsis',
      WebkitBoxOrient: 'vertical',
      WebkitLineClamp: 3,
      wordBreak: 'break-word',
    },
    tablet: {},
    laptop: {
      ...typography.labelLg,
      display: 'block',
      whiteSpace: 'nowrap',
      overflow: 'hidden',
      textOverflow: 'ellipsis',
      wordBreak: 'unset',
      WebkitBoxOrient: 'unset',
      WebkitLineClamp: 'unset',
      maxHeight: 'unset',
    },
  }),
});

export const cardDescription = style({
  ...typography.bodyMd,
  overflow: 'hidden',
  textOverflow: 'ellipsis',
  width: '100%',
  color: themeVars.container.foreground.secondary,

  ...responsiveStyle({
    mobile: {
      marginTop: space.s4,
      display: '-webkit-box',
      WebkitBoxOrient: 'vertical',
      WebkitLineClamp: 3,
      wordBreak: 'break-word',
    },
    tablet: {},
    laptop: {
      display: 'block',
      marginTop: 0,
      width: '90%',
      maxHeight: 'unset',
      WebkitBoxOrient: 'unset',
      WebkitLineClamp: 'unset',
      wordBreak: 'unset',
      whiteSpace: 'nowrap',
    },
  }),
});

export const streamCardFooter = style([
  {
    display: 'flex',
    flexDirection: 'column',
    gap: space.s10,
  },
  responsiveStyle({
    mobile: {
      marginTop: 'auto',
    },
    tablet: {},
    laptop: {
      marginTop: 0,
    },
  }),
]);

export const streamCardFooterAuditory = style({
  display: 'flex',
  alignItems: 'center',
  gap: space.s20,
  justifyContent: 'space-between',
});

export const streamStateIndicator = style([
  {
    width: 9,
    height: 9,
    borderRadius: 9999,
    flexShrink: 0,
    background: themeVars.color.surface.secondary.active,

    selectors: {
      ['&[data-active]']: {
        background: themeVars.container.foreground.success,
      },
    },
  },
]);

export const stateContainer = style([
  {
    display: 'flex',
    gap: space.s8,
    justifyContent: 'space-between',
  },
]);

export const streamCardUsernameContainer = style({
  ...responsiveStyle({
    mobile: {
      width: 'auto',
    },
    tablet: {},
    laptop: {
      width: 100,
    },
  }),
});

export const featuredBadge = style({
  selectors: {
    '&:is(&)': {
      ...typography.labelSm,
      padding: `${space.s4}px ${space.s12}px`,
    },
  },
});

export const streamStateTagContainer = style([
  {
    width: 124,
  },
]);

export const streamFollowersContainer = style([
  {
    width: 42,
  },
]);

export const copyIdButton = style({
  opacity: 0,
  overflow: 'hidden',
  transition: 'opacity 0.2s ease',
  whiteSpace: 'nowrap',

  selectors: {
    [`${streamCard}:hover &`]: {
      opacity: 1,
    },
  },
});
